import React, { useReducer, useEffect } from 'react'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button ,Divider} from '@heroui/react'

// Importar las constantes necesarias
import {
  iconosDisponibles,
  divisas,
  paisesBancarios,
  tiposCuenta,
  codigosPais,
  tiposEnlaces
} from './ConstantesEnlaces'

// Importar componentes de campos específicos
import CamposCuentaBancaria from './CamposCuentaBancaria'
import CamposWhatsApp from './CamposWhatsApp'
import CamposProducto from './CamposProducto'
import CamposUbicacion from './CamposUbicacion'


// Estado inicial del enlace
const estadoInicialEnlace = {
  titulo: '',
  url: '',
  descripcion: '',
  icono: 'fas fa-link',
  tipo: 'normal',
  precio: '',
  divisa: 'USD',
  descuento: '', // Nuevo campo para descuento porcentual
  tab: '',
  // Campos específicos para cuenta bancaria
  datosBancarios: {
    nombreBanco: '',
    paisBanco: 'MX',
    numeroCuenta: '',
    tipoCuenta: 'corriente',
    clabe: '',
    nombreTitular: '',
    identificacionTitular: ''
  },
  // Campos específicos para WhatsApp
  codigoPais: '+52',
  numeroWhatsApp: '',
  mensajePredefinido: '',
  // Campos específicos para producto
  categoria: '',
  stock: '',
  disponible: true,
  // Campos específicos para ubicación
  enlaceGoogleMaps: '',
  nombreLugar: '',
  direccion: '',
  latitud: '',
  longitud: '',
  // Campos de imagen
  tieneImagen: false,
  mostrarFotoGrande: false
}

// Reducer para manejar el estado del modal
const modalReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ENLACE':
      return {
        ...state,
        enlace: { ...state.enlace, ...action.payload }
      }
    case 'RESET_ENLACE':
      return {
        ...state,
        enlace: { ...estadoInicialEnlace, ...action.payload }
      }
    case 'SET_IMAGEN_PREVIEW':
      return {
        ...state,
        imagenPreview: action.payload
      }
    case 'CLEAR_IMAGEN':
      return {
        ...state,
        imagenPreview: null,
        enlace: { ...state.enlace, tieneImagen: false, imagen: '' }
      }
    default:
      return state
  }
}


const ModalEditarEnlace = ({
  isOpen,
  onClose,
  enlaceInicial = null,
  editandoEnlaceIndex = -1,
  onGuardar,
  tabs = [],
  tabSeleccionado = '',
  // Funciones de imagen - solo mantenemos las que realmente necesitamos del padre
  generarEnlaceWhatsApp,
  // Estados de error y carga
  errorMessage = '',
  guardando = false
}) => {

  // Estado interno del modal
  const [state, dispatch] = useReducer(modalReducer, {
    enlace: { ...estadoInicialEnlace },
    imagenPreview: null
  })

  // Inicializar enlace cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      if (enlaceInicial) {
        // Modo edición
        dispatch({ 
          type: 'RESET_ENLACE', 
          payload: { ...enlaceInicial, tab: enlaceInicial.tab || tabSeleccionado }
        })
        // Establecer imagen preview si existe
        if (enlaceInicial.imagen) {
          dispatch({ type: 'SET_IMAGEN_PREVIEW', payload: enlaceInicial.imagen })
        } else {
          dispatch({ type: 'SET_IMAGEN_PREVIEW', payload: null })
        }
      } else {
        // Modo creación
        dispatch({ 
          type: 'RESET_ENLACE', 
          payload: { tab: tabSeleccionado }
        })
        dispatch({ type: 'SET_IMAGEN_PREVIEW', payload: null })
      }
    }
  }, [isOpen, enlaceInicial, tabSeleccionado])

  // Función para manejar cambios en el enlace
  const handleInputChangeEnlace = (campo, valor) => {
    if (campo === 'datosBancarios') {
      dispatch({
        type: 'SET_ENLACE',
        payload: {
          datosBancarios: { ...state.enlace.datosBancarios, ...valor }
        }
      })
    } else {
      dispatch({
        type: 'SET_ENLACE',
        payload: { [campo]: valor }
      })
    }
  }

  // Función para guardar el enlace
  const handleGuardar = async () => {
    try {
      // Preparar el enlace final con la imagen (el padre se encargará de subirla si es necesario)
      const enlaceFinal = {
        ...state.enlace,
        imagen: state.imagenPreview || '',
        tieneImagen: !!state.imagenPreview
      }
      
      console.log('Enlace a guardar:', enlaceFinal)
      
      // Llamar a la función del padre (el padre se encarga de cerrar el modal si es exitoso)
      await onGuardar(enlaceFinal, editandoEnlaceIndex)
    } catch (error) {
      console.error('Error al guardar enlace:', error)
      // No hacer nada aquí, el padre maneja el error y actualiza errorMessage
    }
  }

  // Función para manejar cambio de imagen (completamente interna)
  const handleCambiarImagenInterna = (event) => {
    const file = event.target.files[0]
    if (!file) return

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      console.error('Solo se permiten archivos de imagen')
      return
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('La imagen debe ser menor a 5MB')
      return
    }

    // Crear preview local
    const reader = new FileReader()
    reader.onload = (e) => {
      dispatch({ type: 'SET_IMAGEN_PREVIEW', payload: e.target.result })
      // Actualizar el enlace para indicar que tiene imagen
      handleInputChangeEnlace('tieneImagen', true)
    }
    reader.readAsDataURL(file)
  }

  // Función para eliminar imagen (interna)
  const eliminarImagenInterna = () => {
    dispatch({ type: 'CLEAR_IMAGEN' })
    handleInputChangeEnlace('tieneImagen', false)
    handleInputChangeEnlace('mostrarFotoGrande', false)
  }

  // Función para calcular el precio final con descuento
  const calcularPrecioFinal = () => {
    const precio = parseFloat(state.enlace.precio) || 0
    const descuento = parseFloat(state.enlace.descuento) || 0
    
    if (precio === 0 || descuento === 0) {
      return precio
    }
    
    const descuentoCalculado = precio * (descuento / 100)
    return precio - descuentoCalculado
  }

  // Función para extraer datos de enlace de Google Maps
  const extraerDatosDeEnlaceGoogleMaps = (enlace) => {
    if (!enlace.trim()) return

    try {
      // Limpiar el enlace
      const enlaceLimpio = enlace.trim()
      
      // Verificar si es un enlace de Google Maps válido
      if (!enlaceLimpio.includes('maps.google') && 
          !enlaceLimpio.includes('goo.gl/maps') &&
          !enlaceLimpio.includes('google.com/maps')) {
        return
      }

      // Actualizar el enlace de Google Maps
      handleInputChangeEnlace('enlaceGoogleMaps', enlaceLimpio)

      // Si es un enlace corto (goo.gl), establecer valores por defecto
      if (enlaceLimpio.includes('goo.gl')) {
        if (!state.enlace.nombreLugar) {
          handleInputChangeEnlace('nombreLugar', 'Ubicación en Google Maps')
        }
        if (!state.enlace.direccion) {
          handleInputChangeEnlace('direccion', 'Ver ubicación en Google Maps')
        }
        return
      }

      // Diferentes patrones de enlaces de Google Maps
      const url = new URL(enlaceLimpio)
      let nombreExtraido = ''
      let direccionExtraida = ''
      
      // Patrón 1: ?q=lat,lng (coordenadas directas)
      const qParam = url.searchParams.get('q')
      if (qParam) {
        const coordenadas = qParam.match(/^(-?\d+\.?\d*),(-?\d+\.?\d*)$/)
        if (coordenadas) {
          handleInputChangeEnlace('latitud', coordenadas[1])
          handleInputChangeEnlace('longitud', coordenadas[2])
          nombreExtraido = 'Ubicación por coordenadas'
          direccionExtraida = `Coordenadas: ${coordenadas[1]}, ${coordenadas[2]}`
        } else {
          // Si no son coordenadas directas, usar como dirección
          direccionExtraida = decodeURIComponent(qParam.replace(/\+/g, ' '))
          
          // Extraer nombre del lugar de la dirección
          const partesDir = direccionExtraida.split(',')
          if (partesDir.length > 0) {
            nombreExtraido = partesDir[0].trim()
          }
        }
      }
      
      // Patrón 2: /place/Nombre+del+Lugar/@lat,lng
      const pathname = url.pathname
      const placeMatch = pathname.match(/\/place\/([^\/]+)/)
      if (placeMatch) {
        nombreExtraido = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '))
        
        // Buscar coordenadas en el pathname
        const coordMatch = pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/)
        if (coordMatch) {
          handleInputChangeEnlace('latitud', coordMatch[1])
          handleInputChangeEnlace('longitud', coordMatch[2])
        }
        
        // También buscar en el fragmento (parte después de #)
        if (url.hash) {
          const hashCoordMatch = url.hash.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/)
          if (hashCoordMatch) {
            handleInputChangeEnlace('latitud', hashCoordMatch[1])
            handleInputChangeEnlace('longitud', hashCoordMatch[2])
          }
        }
      }
      
      // Patrón 3: /search/dirección
      const searchMatch = pathname.match(/\/search\/(.+)/)
      if (searchMatch) {
        direccionExtraida = decodeURIComponent(searchMatch[1].replace(/\+/g, ' '))
        
        // Usar la dirección como nombre si no hay nombre
        if (!nombreExtraido) {
          const partesDir = direccionExtraida.split(',')
          if (partesDir.length > 0) {
            nombreExtraido = partesDir[0].trim()
          }
        }
      }
      
      // Patrón 4: /maps/dir/origen/destino
      const dirMatch = pathname.match(/\/maps\/dir\/[^\/]+\/(.+)/)
      if (dirMatch) {
        direccionExtraida = decodeURIComponent(dirMatch[1].replace(/\+/g, ' '))
        
        if (!nombreExtraido) {
          const partesDir = direccionExtraida.split(',')
          if (partesDir.length > 0) {
            nombreExtraido = partesDir[0].trim()
          }
        }
      }
      
      // Establecer valores extraídos
      if (nombreExtraido && !state.enlace.nombreLugar) {
        handleInputChangeEnlace('nombreLugar', nombreExtraido)
      }
      
      if (direccionExtraida && !state.enlace.direccion) {
        handleInputChangeEnlace('direccion', direccionExtraida)
      }
      
      // Si no se pudo extraer nada, usar valores por defecto
      if (!nombreExtraido && !state.enlace.nombreLugar) {
        handleInputChangeEnlace('nombreLugar', 'Ubicación en Google Maps')
      }
      
      if (!direccionExtraida && !state.enlace.direccion) {
        handleInputChangeEnlace('direccion', 'Ver ubicación en Google Maps')
      }
      
    } catch (error) {
      console.error('Error al parsear enlace de Google Maps:', error)
      // Si hay error, al menos guardar el enlace original
      handleInputChangeEnlace('enlaceGoogleMaps', enlace)
      
      // Dar valores por defecto si no hay datos
      if (!state.enlace.nombreLugar) {
        handleInputChangeEnlace('nombreLugar', 'Ubicación en Google Maps')
      }
      if (!state.enlace.direccion) {
        handleInputChangeEnlace('direccion', 'Ver ubicación en Google Maps')
      }
    }
  }
  
  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <i className="fas fa-link text-primary"></i>
            <span>{editandoEnlaceIndex === -1 ? 'Nuevo Enlace' : 'Editar Enlace'}</span>
          </div>
        </ModalHeader>
        <ModalBody>
          {/* Mensaje de error */}
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <i className="fas fa-exclamation-circle"></i>
                <span>{errorMessage}</span>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Selector de tab */}
            {tabs && tabs.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <i className="fas fa-folder text-purple-600"></i>
                  Categoría (Tab)
                </h4>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Selecciona el tab
                  </label>
                  <select
                    value={state.enlace.tab || tabSeleccionado}
                    onChange={(e) => handleInputChangeEnlace('tab', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Elige dónde aparecerá este enlace</option>
                    {tabs.map((tab) => (
                      <option key={tab.id} value={tab.id}>
                        {tab.nombre}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500">El enlace se mostrará en esta categoría</p>
                </div>
              </div>
            )}
            
            {tabs && tabs.length > 0 && (
              <Divider />
            )}
            
            {/* Selector de tipo de enlace */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                <i className="fas fa-list-ul text-blue-600"></i>
                Tipo de Enlace
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {tiposEnlaces.map((tipo) => (
                  <div
                    key={tipo.id}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      state.enlace.tipo === tipo.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleInputChangeEnlace('tipo', tipo.id)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${tipo.icono} text-lg`} style={{ 
                        color: tipo.color === 'blue' ? '#3B82F6' : 
                               tipo.color === 'green' ? '#10B981' : 
                               tipo.color === 'success' ? '#25D366' : '#F59E0B' 
                      }}></i>
                      <span className="font-medium text-sm">{tipo.nombre}</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-tight">{tipo.descripcion}</p>
                  </div>
                ))}
              </div>
            </div>

            <Divider />

            {/* Campos básicos */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Título <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <i className="fas fa-heading absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Nombre del enlace"
                  value={state.enlace.titulo}
                  onChange={(e) => handleInputChangeEnlace('titulo', e.target.value)}
                  required
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Campos específicos según el tipo de enlace */}
            {state.enlace.tipo === 'cuenta_bancaria' ? (
              // Campos para cuenta bancaria
              <CamposCuentaBancaria state={state} handleInputChangeEnlace={handleInputChangeEnlace} paisesBancarios={paisesBancarios} tiposCuenta={tiposCuenta} />
            ) : state.enlace.tipo === 'whatsapp' ? (
              // Campos para WhatsApp
              <CamposWhatsApp state={state} handleInputChangeEnlace={handleInputChangeEnlace} codigosPais={codigosPais} generarEnlaceWhatsApp={generarEnlaceWhatsApp} />
            ) : state.enlace.tipo === 'producto' ? (
              // Campos para producto
              <CamposProducto state={state} handleInputChangeEnlace={handleInputChangeEnlace} divisas={divisas} />
            ) : state.enlace.tipo === 'ubicacion' ? (
              // Campos para ubicación - Simplificado
              <CamposUbicacion state={state} handleInputChangeEnlace={handleInputChangeEnlace} extraerDatosDeEnlaceGoogleMaps={extraerDatosDeEnlaceGoogleMaps} />
            ) : (
              // Campo URL normal
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  URL <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <i className="fas fa-link absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="url"
                    placeholder="https://ejemplo.com"
                    value={state.enlace.url}
                    onChange={(e) => handleInputChangeEnlace('url', e.target.value)}
                    required
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Descripción
              </label>
              <div className="relative">
                <i className="fas fa-align-left absolute left-3 top-3 text-gray-400"></i>
                <textarea
                  placeholder="Descripción opcional del enlace"
                  value={state.enlace.descripcion}
                  onChange={(e) => handleInputChangeEnlace('descripcion', e.target.value)}
                  rows={3}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="space-y-2 flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  Icono
                </label>
                <select
                  value={state.enlace.icono}
                  onChange={(e) => handleInputChangeEnlace('icono', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecciona un icono</option>
                  {iconosDisponibles.map((icono) => (
                    <option key={icono.id} value={icono.id}>
                      {icono.nombre}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Precio solo para enlaces normales */}
              {state.enlace.tipo === 'normal' && (
                <div className="space-y-2 flex-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Precio (opcional)
                  </label>
                  <div className="relative">
                    <i className="fas fa-dollar-sign absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={state.enlace.precio}
                      onChange={(e) => handleInputChangeEnlace('precio', e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Descuento y precio final solo para enlaces normales con precio */}
            {state.enlace.tipo === 'normal' && state.enlace.precio && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Descuento (%)
                  </label>
                  <div className="relative">
                    <i className="fas fa-percentage absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="0"
                      value={state.enlace.descuento}
                      onChange={(e) => handleInputChangeEnlace('descuento', e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Precio Final
                  </label>
                  <div className="relative">
                    <i className="fas fa-tag absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      value={calcularPrecioFinal().toFixed(2)}
                      readOnly
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 font-semibold"
                    />
                  </div>
                  {state.enlace.descuento && parseFloat(state.enlace.descuento) > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-red-600 font-medium">
                        -{parseFloat(state.enlace.descuento).toFixed(1)}%
                      </span>
                      <span className="text-gray-500 line-through">
                        {parseFloat(state.enlace.precio).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Moneda solo para enlaces normales con precio */}
            {state.enlace.tipo === 'normal' && state.enlace.precio && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Moneda
                </label>
                <select
                  value={state.enlace.divisa}
                  onChange={(e) => handleInputChangeEnlace('divisa', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {divisas.map((divisa) => (
                    <option key={divisa.codigo} value={divisa.codigo}>
                      {divisa.simbolo} {divisa.nombre}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <Divider className="my-4" />
            
            {/* Sección de imagen - Para todos los tipos excepto cuenta bancaria */}
            {state.enlace.tipo !== 'cuenta_bancaria' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-semibold text-gray-700">Imagen del Enlace</h4>
                  <p className="text-sm text-gray-500">Opcional</p>
                </div>
                
                {/* Vista previa de imagen */}
                {state.imagenPreview && (
                  <div className="relative">
                    <img
                      src={state.imagenPreview}
                      alt="Vista previa"
                      className="w-full max-w-xs mx-auto rounded-lg shadow-md"
                      style={{ maxHeight: '200px', objectFit: 'cover' }}
                    />
                    <button
                      onClick={eliminarImagenInterna}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <i className="fas fa-trash text-xs"></i>
                    </button>
                  </div>
                )}
                

                
                {/* Selector de imagen */}
                {!state.imagenPreview && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <i className="fas fa-image text-3xl text-gray-400 mb-2"></i>
                    <p className="text-sm text-gray-600 mb-3">
                      Arrastra una imagen aquí o haz clic para seleccionar
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCambiarImagenInterna}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                )}
                
                {/* Opciones de imagen */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <i className="fas fa-expand-arrows-alt text-gray-500"></i>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Mostrar {state.enlace.tieneImagen ? 'imagen' : 'icono'} en grande</p>
                      <p className="text-xs text-gray-500">La imagen se mostrará más prominente</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={state.enlace.mostrarFotoGrande}
                      onChange={(e) => handleInputChangeEnlace('mostrarFotoGrande', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            variant="light"
            onClick={onClose}
            disabled={guardando}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleGuardar}
            isLoading={guardando}
          >
            {editandoEnlaceIndex === -1 ? 'Crear Enlace' : 'Guardar Cambios'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ModalEditarEnlace 